import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useGetStreamingToken } from "@/api/hooks";
import { useNotification } from "@/providers/NotificationProvider";
import { useRealtimeTranscription } from "@/hooks/useRealtimeTranscription";
import { notifications, ui } from "@/strings";
import { buttonVariants } from "@markeroo/theme";
import { parseBoldText } from "@/utils/textFormatting";
import PiMicMicrophoneSolid from "@markeroo/icons/src/solid/media/PiMicMicrophoneSolid";
import PiStopBigSolid from "@markeroo/icons/src/solid/media/PiStopBigSolid";
import PiMultipleCrossCancelDefaultStroke from "@markeroo/icons/src/stroke/maths/PiMultipleCrossCancelDefaultStroke";
import { Spinner } from "@/components/Spinner";

// Animation configuration for oscillating wave visualizer
const WAVE_CONFIG = {
  BAR_COUNT: 7,
  FFT_SIZE: 256,
  WAVE_SPEED: 3, // Oscillations per second when speaking
  IDLE_HEIGHT: 20, // Height when not speaking
  MIN_HEIGHT: 15, // Absolute minimum clamped height
  OSCILLATION_RANGE: 15, // Height variation from sine wave when speaking
  VOLUME_MULTIPLIER: 50, // How much volume affects height
  VOLUME_SMOOTHING: 0.25, // Smoothing factor (0 = instant, 1 = no change)
  RMS_AMPLIFICATION: 5, // Amplification factor for quiet audio
  VOICE_THRESHOLD: 0.05, // Minimum volume to trigger wave animation
} as const;

interface VoiceDictationButtonProps {
  onTranscriptionComplete: (text: string) => Promise<void> | void;
  isProcessing?: boolean;
}

export function VoiceDictationButton({
  onTranscriptionComplete,
  isProcessing = false,
}: VoiceDictationButtonProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [barHeights, setBarHeights] = useState<number[]>(
    Array(WAVE_CONFIG.BAR_COUNT).fill(WAVE_CONFIG.IDLE_HEIGHT)
  );

  // Animation state refs
  const volumeRef = useRef<number>(0);
  const smoothedVolumeRef = useRef<number>(0);
  const lastTimestampRef = useRef<number>(0);

  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const transcriptRef = useRef("");
  const waitingForFinalRef = useRef(false);

  const { show } = useNotification();

  const getTokenMutation = useGetStreamingToken();

  // Realtime transcription hook
  const { startSession, stopSession } =
    useRealtimeTranscription({
      onTranscriptUpdate: (text, isFinal) => {
        transcriptRef.current = text;

        // If we're waiting for final transcript and this is it, submit now
        if (waitingForFinalRef.current && isFinal) {
          waitingForFinalRef.current = false;
          const finalText = text.trim();
          if (finalText) {
            void onTranscriptionComplete(finalText);
          }
          // Reset for next recording
          transcriptRef.current = "";
          setIsRecording(false);
        }
      },
      onError: (errorMsg) => {
        setError(errorMsg);
        show(notifications.voice.streamingFailed, "error");
        cleanup();
      },
      onSessionEnd: () => {
        // Session ended - submit accumulated transcript if we have one
        if (waitingForFinalRef.current && transcriptRef.current.trim()) {
          const finalText = transcriptRef.current.trim();
          void onTranscriptionComplete(finalText);
        }
        // Reset state
        waitingForFinalRef.current = false;
        transcriptRef.current = "";
        setIsRecording(false);
      },
    });

  // Cleanup function
  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }
    analyserRef.current = null;
    volumeRef.current = 0;
    smoothedVolumeRef.current = 0;
    lastTimestampRef.current = 0;
    setBarHeights(Array(WAVE_CONFIG.BAR_COUNT).fill(WAVE_CONFIG.IDLE_HEIGHT));
    setIsRecording(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  const startRecording = async () => {
    setError(null);

    try {
      // 1. Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      // 2. Setup audio analyzer for wave visualization
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = WAVE_CONFIG.FFT_SIZE;
      analyser.smoothingTimeConstant = 0; // No built-in smoothing (we apply our own)

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // 3. Time domain data monitoring loop for oscillating wave bars
      const timeDataArray = new Uint8Array(analyser.fftSize);

      const updateWaveAnimation = (timestamp: number) => {
        // Check mounted and analyser status first to prevent operations on unmounted component
        if (!isMountedRef.current || !analyserRef.current) {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          return;
        }

        // Get time-domain data for RMS volume calculation
        analyserRef.current.getByteTimeDomainData(timeDataArray);

        // Calculate RMS (root mean square) for overall volume level
        let sum = 0;
        for (let i = 0; i < timeDataArray.length; i++) {
          const normalized = (timeDataArray[i] - 128) / 128;
          sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / timeDataArray.length);
        volumeRef.current = Math.min(1, rms * WAVE_CONFIG.RMS_AMPLIFICATION);

        // Apply exponential smoothing for less jittery response
        smoothedVolumeRef.current =
          smoothedVolumeRef.current * WAVE_CONFIG.VOLUME_SMOOTHING +
          volumeRef.current * (1 - WAVE_CONFIG.VOLUME_SMOOTHING);

        // Calculate heights for all bars
        const newHeights: number[] = [];
        const isVoiceDetected = smoothedVolumeRef.current > WAVE_CONFIG.VOICE_THRESHOLD;

        if (isVoiceDetected) {
          // Voice detected: show oscillating wave with volume-based amplitude
          const timeInSeconds = timestamp / 1000;
          const waveTime = timeInSeconds * WAVE_CONFIG.WAVE_SPEED * Math.PI * 2;

          for (let i = 0; i < WAVE_CONFIG.BAR_COUNT; i++) {
            // Phase offset creates traveling wave effect
            const phaseOffset = (i / WAVE_CONFIG.BAR_COUNT) * Math.PI * 2;
            const oscillation = Math.sin(waveTime + phaseOffset);

            // Base height from oscillation, scaled by volume
            const oscillationHeight = oscillation * WAVE_CONFIG.OSCILLATION_RANGE * smoothedVolumeRef.current;

            // Add volume-based amplitude
            const volumeBoost = smoothedVolumeRef.current * WAVE_CONFIG.VOLUME_MULTIPLIER;
            const totalHeight = Math.min(100, Math.max(WAVE_CONFIG.MIN_HEIGHT, WAVE_CONFIG.IDLE_HEIGHT + oscillationHeight + volumeBoost));

            newHeights.push(totalHeight);
          }
        } else {
          // No voice: show static idle bars
          for (let i = 0; i < WAVE_CONFIG.BAR_COUNT; i++) {
            newHeights.push(WAVE_CONFIG.IDLE_HEIGHT);
          }
        }

        setBarHeights(newHeights);
        animationFrameRef.current = requestAnimationFrame(updateWaveAnimation);
      };

      animationFrameRef.current = requestAnimationFrame(updateWaveAnimation);

      // 4. Fetch streaming token from backend
      setIsRecording(true);
      const { token } = await getTokenMutation.mutateAsync();

      // 5. Start realtime transcription session
      await startSession(token, stream);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "NotAllowedError") {
        setError(notifications.voice.microphonePermissionDenied);
        show(notifications.voice.microphonePermissionDenied, "error");
      } else if (err instanceof Error) {
        setError(err.message);
        show(notifications.voice.tokenFetchFailed, "error");
      } else {
        setError(notifications.voice.recordingFailed);
        show(notifications.voice.recordingFailed, "error");
      }
      cleanup();
    }
  };

  const stopRecording = () => {
    // Mark that we're waiting for the final transcript from AssemblyAI
    waitingForFinalRef.current = true;

    // Stop the session - this will trigger the WebSocket to close
    // and AssemblyAI will send the final transcript message
    stopSession();

    // Don't call cleanup() here - let onSessionEnd handle it after final transcript arrives
    // Cleanup will be triggered by the hook when the WebSocket closes and final transcript is received
  };

  const cancelRecording = () => {
    // Clear transcript refs to prevent auto-submit
    transcriptRef.current = "";
    waitingForFinalRef.current = false; // Don't wait for final

    // Stop the session and cleanup
    stopSession();
    cleanup();

    // Show cancellation message
    show(notifications.voice.recordingCancelled, "info");
  };

  const disabled = isProcessing || getTokenMutation.isPending;
  const isFetching = getTokenMutation.isPending;

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* Error message */}
      {error && (
        <div className="rounded-md bg-semantic-error-light border border-semantic-error p-3 text-sm text-semantic-error">
          {error}
        </div>
      )}

      {/* Recorder area - bordered box */}
      <div className="flex-1 flex flex-col rounded-lg border-2 border-hover bg-surface-secondary p-6">
        {/* Explainer text - fixed distance from top */}
        <p className="text-center text-base font-medium text-theme-secondary max-w-md mx-auto mb-8">
          {isRecording ? ui.labels.voiceRecordingHint : parseBoldText(ui.labels.voiceDictationExplainer)}
        </p>

        {/* Content area - centered */}
        <div className="flex-1 flex flex-col items-center justify-center gap-6">

        {/* Oscillating wave visualization during recording */}
        {isRecording && (
          <div className="flex items-end justify-center gap-1 h-16">
            {barHeights.map((height, index) => (
              <motion.div
                key={index}
                animate={{ height: `${height}%` }}
                transition={{ duration: 0.05, ease: "linear" }}
                className="w-2 bg-semantic-error rounded-full"
              />
            ))}
          </div>
        )}

        {/* Recording controls */}
        {!isFetching && (
          <div className="flex flex-col items-center gap-3">
            {/* Main recorder button - centered */}
            <button
              type="button"
              onClick={isRecording ? stopRecording : () => { void startRecording(); }}
              disabled={disabled}
              className={`w-24 h-24 rounded-full flex items-center justify-center text-h1 shadow-lg ${
                isRecording
                  ? "bg-semantic-error hover:bg-semantic-error-dark text-white"
                  : buttonVariants.primary
              }`}
              aria-label={isRecording ? ui.actions.stopStreaming : ui.actions.startDictation}
            >
              {isRecording ? (
                <PiStopBigSolid size={48} aria-hidden="true" />
              ) : (
                <PiMicMicrophoneSolid size={48} aria-hidden="true" />
              )}
            </button>

            {/* Cancel button below - only show when recording */}
            {isRecording && (
              <button
                type="button"
                onClick={cancelRecording}
                className={buttonVariants.ghostDanger}
                aria-label={ui.actions.cancel}
              >
                <PiMultipleCrossCancelDefaultStroke size={20} className="mr-1" aria-hidden="true" />
                {ui.actions.cancel}
              </button>
            )}
          </div>
        )}

        {/* Connecting state */}
        {isFetching && (
          <div className="flex flex-col items-center gap-3">
            <Spinner size="xl" className="text-brand-600" />
            <p className="text-sm text-theme-secondary">{ui.loading.connectingToStream}</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
